/**
 * The errands between buying a plan and building it: the lumber yard, with the shopping
 * list ticked off line by line as the cart fills, and the drive home with the dog at
 * the back window.
 *
 * Every film that builds something has them, and what changes is what is on the list: so
 * a film hands in its `Shopping` — what lands on the cart, under which line of the bill,
 * and when — and the aisle, the cart and the car are drawn here, once. Each line is
 * something the plan lists, on its bill or its consumables page; its numbers never are,
 * and because a shed's list always runs past the bill (the shingles are a consumable), the
 * clipboard calls it what it is: the shopping list.
 */

import type { View } from "./film.js";
import type { Ink } from "./ink.js";
import {
  GROUND,
  car,
  cart,
  checklist,
  cloud,
  dust,
  landscape,
  load,
  racks,
  shoot,
  sign,
  speedLines,
  tree,
  type Camera,
  type Load,
} from "./props.js";
import { STAND, drawFigure, walk, type Pose } from "./rig.js";
import type { Mix } from "./sound.js";
import { ease, mix, span } from "./timing.js";

// ---- the lumber yard -----------------------------------------------------------------------

/** One line of the bill, landing on the cart. */
export interface Purchase {
  kind: Load;
  /** How tall it stacks on the cart, in stage units. */
  height: number;
  /** Its line on the checklist. */
  line: string;
  /** When it lands, in the shop scene's seconds. */
  at: number;
}

export interface Shopping {
  list: readonly Purchase[];
  /** The cart's push down the aisle. */
  aisle: readonly [number, number];
}

/**
 * The aisle's signs, in the order the camera passes them. Spaced so that, as it travels the
 * aisle, one is always hanging inside the stage in every shape, clear of the caption above
 * and the clipboard beside it.
 */
const SIGNS = [
  { x: -60, word: "LUMBER" },
  { x: 200, word: "ROOFING" },
] as const;

export function shopScene(ink: Ink, view: View, u: number, shopping: Shopping): void {
  const p = ink.palette;
  const walking = span(u, ...shopping.aisle);
  const x = mix(-430, 170, walking);
  const camera: Camera = { x: mix(-170, 120, walking), y: 70, zoom: 1.4 };
  shoot(
    ink,
    camera,
    (v) => {
      ink.wash(
        [
          { x: v.left - 20, y: GROUND },
          { x: v.right + 20, y: GROUND },
          { x: v.right + 20, y: v.bottom + 20 },
          { x: v.left - 20, y: v.bottom + 20 },
        ],
        p.shadow,
        0.9,
        0,
      );
      racks(ink, v, GROUND - 10);
      ink.line(v.left - 20, GROUND, v.right + 20, GROUND, { width: 2.6 });
      for (const [i, each] of SIGNS.entries()) {
        sign(ink, each.x, -120, each.word, Math.sin(u * 3 + i) * 0.03);
      }

      const cartX = x + 40;
      const grip = cart(ink, cartX, GROUND, x / 12);
      // The bill, loaded line by line.
      let stack = GROUND - 32;
      shopping.list.forEach((item, i) => {
        const k = span(u, item.at - 0.35, item.at);
        if (k <= 0) {
          return;
        }
        const restX = cartX + 95;
        const fromX = restX + 160;
        const fromY = GROUND - 150 - (i % 3) * 60;
        const lx = mix(fromX, restX, ease.out(k));
        const ly = mix(fromY, stack, ease.in(k)) - Math.sin(k * Math.PI) * 50;
        load(ink, item.kind, lx, ly, mix(0.5, 0, ease.out(k)));
        if (k >= 1) {
          stack -= item.height;
        }
      });
      const pose: Pose = { ...walk(x / 58), lean: 0.22 };
      drawFigure(ink, pose, {
        x,
        ground: GROUND,
        facing: 1,
        reachA: grip,
        reachB: { x: grip.x - 2, y: grip.y + 6 },
        eyes: "open",
        mouth: "smile",
      });
    },
    view,
  );
  checklist(
    ink,
    -420,
    -300,
    "Shopping list",
    shopping.list.map((item) => item.line),
    shopping.list.map((item) => span(u, item.at, item.at + 0.25)),
  );
}

/** How each kind of load sounds landing on the cart: its weight, and how flat it slaps. */
const LANDING: Readonly<Record<Load, { weight: number; slap: number }>> = {
  concrete: { weight: 1.5, slap: 0.12 },
  studs: { weight: 1, slap: 0.2 },
  treated: { weight: 1.05, slap: 0.2 },
  joists: { weight: 1.1, slap: 0.2 },
  plywood: { weight: 0.9, slap: 0.55 },
  siding: { weight: 0.9, slap: 0.55 },
  shingles: { weight: 1.2, slap: 0.2 },
};

/**
 * The cart down the aisle, each load landing on it, the pen ticking its line, and a note
 * a line from `notes` — a step up the film's own scale — for each.
 */
export function shopSound(mix: Mix, at: number, shopping: Shopping, notes: readonly number[]): void {
  const [pushes, pushed] = shopping.aisle;
  mix.roll(at + pushes, pushed - pushes, { gain: 0.7 });
  shopping.list.forEach((item, i) => {
    const lands = item.at;
    const { weight, slap } = LANDING[item.kind];
    mix.thud(at + lands, { gain: 0.65, weight, pan: 0.15 });
    mix.slap(at + lands, { gain: slap, pan: 0.15 });
    mix.scribble(at + lands + 0.03, { gain: 0.6, pan: -0.55 });
    mix.mallet(at + lands + 0.05, notes[i % notes.length]!, 0.5, { pan: -0.3 });
  });
}

// ---- the drive home ------------------------------------------------------------------------

/** The drive home: the car pulls away and off the frame, in the scene's seconds. */
export const AWAY = [1.05, 1.6] as const;

export function driveScene(ink: Ink, view: View, u: number): void {
  const p = ink.palette;
  const off = ease.in(span(u, ...AWAY));
  const carX = mix(-40, 760, off);
  shoot(
    ink,
    { x: -10, y: 140, zoom: 1.45 },
    (v) => {
      landscape(ink, v, 150);
      cloud(ink, 240 - u * 40, -250, 46);
      cloud(ink, -300 - u * 30, -190, 34);
      // Trees going by, the far ones slower.
      for (let i = 0; i < 7; i += 1) {
        const tx = ((((i * 340 - u * 620) % 2380) + 2380) % 2380) - 1190;
        const { ctx } = ink;
        ctx.save();
        ctx.translate(tx, 160);
        const size = 0.55 + (i % 3) * 0.12;
        ctx.scale(size, size);
        tree(ink, 0, 0, 0);
        ctx.restore();
      }
      ink.wash(
        [
          { x: v.left - 20, y: 222 },
          { x: v.right + 20, y: 222 },
          { x: v.right + 20, y: 320 },
          { x: v.left - 20, y: 320 },
        ],
        p.roof,
        0.85,
        0,
      );
      ink.line(v.left - 20, 222, v.right + 20, 222, { width: 2.6 });
      ink.line(v.left - 20, 320, v.right + 20, 320, { width: 2.6 });
      for (let dx = -900; dx < 900; dx += 120) {
        const dash = ((dx - u * 900) % 1800) + 900 - 900;
        ink.line(dash, 272, dash + 60, 272, { width: 5, color: p.trim, overshoot: false });
      }
      const speed = 1 + off * 2;
      const driver = car(ink, carX, 300, u, speed);
      drawFigure(ink, STAND, { x: driver.x, y: driver.y + 72, facing: 1, headOnly: true, look: 0.8, eyes: "happy", mouth: "grin" });
      // The dog at the back window, ears in the wind.
      const head = { x: carX - 64, y: 300 - 26 - 92 };
      ink.circle(head.x, head.y, 12, { fill: p.paper, width: 2.8, offset: 0 });
      ink.ellipse(head.x + 13, head.y + 4, 7, 5, { fill: p.paper, width: 2.4, offset: 0 });
      ink.dot(head.x + 19, head.y + 2, 2.4);
      ink.dot(head.x + 5, head.y - 3, 2);
      const flap = Math.sin(u * 30) * 4;
      ink.shape(
        [
          { x: head.x - 6, y: head.y - 10 },
          { x: head.x + 2, y: head.y - 11 },
          { x: head.x - 26, y: head.y - 14 + flap },
        ],
        { fill: p.woodDark, width: 2.2, offset: 0 },
      );
      ink.ellipse(head.x + 14, head.y + 13, 3, 6, { fill: p.signal, width: 1.4, offset: 0 });
      speedLines(ink, carX - 190, 200, u, 4, 80 + off * 120);
      dust(ink, carX - 150, 296, (u * 3) % 1, 0.8);
    },
    view,
  );
}

/** The engine for the scene's `length` in seconds, pulling away, and the air behind it. */
export function driveSound(mix: Mix, at: number, length: number): void {
  mix.engine(at, length + 0.15, AWAY[0], { gain: 1 });
  mix.whoosh(at + AWAY[0] + 0.1, AWAY[1] - AWAY[0], { from: 300, to: 1200, gain: 0.35, pan: 0.6 });
}
